import type { TableColumn, TableElement } from "../core/types";
import { toCssLength } from "../core/units";
import { escapeHtml } from "../utils/html";

function resolveCellValue<Row extends object>(column: TableColumn<Row>, row: Row, rowIndex: number): string {
  const rawValue = row[column.key as keyof Row];
  const rendered = column.render ? column.render(rawValue, row, rowIndex) : rawValue;
  return rendered === undefined || rendered === null ? "" : escapeHtml(String(rendered));
}

function resolveColumnWidth<Row extends object>(column: TableColumn<Row>): string | undefined {
  if (!column.width) {
    return undefined;
  }

  return typeof column.width === "string" ? column.width : toCssLength(column.width);
}

// Render a semantic table that can participate in automatic pagination. The
// caller may opt out of repeated headers if the print target prefers it.
export function renderTableElement<Row extends object>(element: TableElement<Row>): string {
  const columns = element.columns
    .map((column) => {
      const width = resolveColumnWidth(column);
      return width ? `<col style="width:${width}" />` : "<col />";
    })
    .join("");

  const header = element.columns
    .map((column) => {
      const classes = ["printer-table__header", column.headerClassName].filter(Boolean).join(" ");
      const align = column.align ? ` style="text-align:${column.align}"` : "";
      return `<th class="${classes}"${align}>${escapeHtml(column.label)}</th>`;
    })
    .join("");

  const rows = element.rows.length
    ? element.rows
        .map((row, rowIndex) => {
          const cells = element.columns
            .map((column) => {
              const classes = ["printer-table__cell", column.cellClassName].filter(Boolean).join(" ");
              const align = column.align ? ` style="text-align:${column.align}"` : "";
              return `<td class="${classes}"${align}>${resolveCellValue(column, row, rowIndex)}</td>`;
            })
            .join("");

          return `<tr class="printer-table__row">${cells}</tr>`;
        })
        .join("")
    : `<tr class="printer-table__row printer-table__row--empty"><td class="printer-table__cell" colspan="${element.columns.length}">${escapeHtml(element.emptyMessage ?? "No data")}</td></tr>`;

  const stripedClass = element.striped ? " printer-table--striped" : "";
  const repeatHeaderClass = element.repeatHeaderOnPageBreak === false ? " printer-table--no-repeat-header" : "";
  const shouldShowHeader = element.showHeader !== false;
  const headerMarkup = shouldShowHeader ? `<thead><tr>${header}</tr></thead>` : "";

  return `<table class="printer-table${stripedClass}${repeatHeaderClass}">
    <colgroup>${columns}</colgroup>
    ${headerMarkup}
    <tbody>${rows}</tbody>
  </table>`;
}
